import { DatePipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CurrencySymbolComponent } from '../../../core/components/currency-symbol/currency-symbol.component';
import { AmountPipe } from '../../../core/pipes/amount.pipe';
import { AuthService } from '../../auth/auth.service';
import { FilterConfig, GlobalFilterPanelComponent } from '../components/global-filter-panel/global-filter-panel.component';
import { DashboardService } from '../dashboard.service';
import { DashboardEngineService } from '../services/dashboard-engine.service';
import { ActivityTimelineComponent } from '../widgets/shared/activity-timeline/activity-timeline.component';
import { ApprovalQueueListComponent } from '../widgets/shared/approval-queue-list/approval-queue-list.component';
import { ContractExpiryTrackerComponent } from '../widgets/shared/contract-expiry-tracker/contract-expiry-tracker.component';
import { KpiCard, KpiCardGridComponent } from '../widgets/shared/kpi-card-grid/kpi-card-grid.component';

@Component({
  selector: 'app-procurement-dashboard',
  standalone: true,
  imports: [
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    NgClass,
    DatePipe,
    GlobalFilterPanelComponent,
    KpiCardGridComponent,
    ContractExpiryTrackerComponent,
    ApprovalQueueListComponent,
    ActivityTimelineComponent,
    AmountPipe,
    CurrencySymbolComponent,
  ],
  templateUrl: './procurement-dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { style: 'display: block; width: 100%;' },
})
export class ProcurementDashboardComponent {
  private engine = inject(DashboardEngineService);
  protected dashboardSvc = inject(DashboardService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private auth = inject(AuthService);

  readonly dashboardType = 'procurement';
  filters: any = {};

  workOrderOverviewData: any = null;
  pipelineData: any = null;
  spendData: any = null;

  loadingWorkOrderOverview = false;
  loadingPipeline = false;
  loadingSpend = false;

  workOrderKpis: KpiCard[] = [];
  private vendorCount = 0;

  readonly filterConfig: FilterConfig = {
    community: true,
    period: true,
    dateRange: false,
    status: false,
    priority: false,
  };

  onFiltersChanged(f: any): void {
    this.filters = { ...f };
    this.dashboardSvc.activeFilters.set(this.filters);
    this.loadAll();
    this.cdr.markForCheck();
  }

  private loadAll(): void {
    this.loadWorkOrderOverview();
    this.loadVendors();
    this.loadPipeline();
    this.loadSpend();
  }

  private buildWorkOrderKpis(): void {
    const d = this.workOrderOverviewData ?? {};
    this.workOrderKpis = [
      { label: 'Active Work Orders', value: d.active ?? 0, icon: 'description', color: 'text-blue-600', bgColor: 'bg-blue-50', route: '/procurement/work-orders/active' },
      { label: 'Expiring in 30d', value: d.expiring_30d ?? 0, icon: 'warning', color: 'text-orange-600', bgColor: 'bg-orange-50' },
      { label: 'Expiring in 90d', value: d.expiring_90d ?? 0, icon: 'schedule', color: 'text-yellow-600', bgColor: 'bg-yellow-50' },
      { label: 'Already Expired', value: d.expired ?? 0, icon: 'cancel', color: 'text-red-600', bgColor: 'bg-red-50' },
      { label: 'Active Vendors', value: this.vendorCount, icon: 'store', color: 'text-purple-600', bgColor: 'bg-purple-50' },
    ];
  }

  private loadWorkOrderOverview(): void {
    this.loadingWorkOrderOverview = true;
    this.engine
      .getWidgetData(this.dashboardType, 'workOrderOverview', this.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.workOrderOverviewData = res.record?.data?.data ?? res.record?.data ?? res.record ?? {};
          this.buildWorkOrderKpis();
          this.loadingWorkOrderOverview = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadingWorkOrderOverview = false;
          this.cdr.markForCheck();
        },
      });
  }

  private loadVendors(): void {
    this.engine
      .getWidgetData(this.dashboardType, 'vendorCompliance', this.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          const d = res.record?.data?.data ?? res.record?.data ?? res.record ?? {};
          this.vendorCount = d.total_vendors ?? 0;
          this.buildWorkOrderKpis();
          this.cdr.markForCheck();
        },
      });
  }

  private loadPipeline(): void {
    if (!this.hasPipelinePermission) return;
    this.loadingPipeline = true;
    this.engine
      .getWidgetData(this.dashboardType, 'workOrderPipeline', this.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.pipelineData = res.record?.data?.data ?? res.record?.data ?? res.record ?? {};
          this.loadingPipeline = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadingPipeline = false;
          this.cdr.markForCheck();
        },
      });
  }

  private loadSpend(): void {
    if (!this.hasSpendPermission) return;
    this.loadingSpend = true;
    this.engine
      .getWidgetData(this.dashboardType, 'spendAnalysis', this.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.spendData = res.record?.data?.data ?? res.record?.data ?? res.record ?? {};
          this.loadingSpend = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadingSpend = false;
          this.cdr.markForCheck();
        },
      });
  }

  get hasPipelinePermission(): boolean {
    return this.auth.canAccess(['rfp_view']);
  }
  get hasSpendPermission(): boolean {
    return this.auth.canAccess(['accounting_bills_view']);
  }

  getExpiryColor(days: number): string {
    if (days <= 0) return 'text-red-700 bg-red-50 border-red-200';
    if (days <= 30) return 'text-orange-700 bg-orange-50 border-orange-200';
    if (days <= 60) return 'text-yellow-700 bg-yellow-50 border-yellow-200';
    return 'text-green-700 bg-green-50 border-green-200';
  }

  getStatusColor(s: string): string {
    const map: any = { active: 'text-blue-700 bg-blue-50', pending: 'text-yellow-700 bg-yellow-50', completed: 'text-green-700 bg-green-50', cancelled: 'text-gray-600 bg-gray-50' };
    return map[s?.toLowerCase()] ?? 'text-gray-600 bg-gray-50';
  }
}
